import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Incident, ThreatLevel } from '../types';
import { SubscriptionTier } from './SubscriptionModal';

interface LiveCall {
  eid: string;
  lat: number;
  lon: number;
  address: string | null;
  type: string | null;
  level: string | null;
  ts: number;
  text: string;
  time: string;
}

interface FeedItem {
  id: string;
  text: string;
  type: string;
  address: string;
  time: string;
  threatLevel: ThreatLevel;
  lat: number;
  lon: number;
  isArya?: boolean;
  isLive?: boolean;
}

interface Props {
  onNewIncident: (inc: Incident) => void;
  userLocation?: { lat: number; lng: number } | null;
  tier?: SubscriptionTier;
  onUpgradeClick?: () => void;
  onAryaCall?: (loc: { lat: number; lng: number }) => void;
  onCallClick?: (lat: number, lon: number, address: string) => void;
}

// Map priority level → threat
function levelToThreat(level: string | null, type: string | null): ThreatLevel {
  if (level === '1') return ThreatLevel.CRITICAL;
  if (level === '2') return ThreatLevel.HIGH;
  if (level === '3') return ThreatLevel.MEDIUM;
  const t = (type || '').toUpperCase();
  if (/HOMICIDE|SHOOTING|STABBING|ROBBERY|ASSAULT|WEAPON/.test(t)) return ThreatLevel.HIGH;
  if (/BURGLARY|THEFT|VEHICLE|DRUG|DUI/.test(t)) return ThreatLevel.MEDIUM;
  return ThreatLevel.LOW;
}

const THREAT_COLOR: Record<ThreatLevel, string> = {
  [ThreatLevel.CRITICAL]: '#ff1a1a',
  [ThreatLevel.HIGH]:     '#ff7700',
  [ThreatLevel.MEDIUM]:   '#ffcc00',
  [ThreatLevel.LOW]:      '#00e676',
};

const LEVEL_LABEL: Record<string, string> = {
  '1': 'EMERGENCY', '2': 'PRIORITY', '3': 'ROUTINE', '4': 'NON-EMERG',
};

const ARYA_SIGHTINGS = [
  { location: { lat: 32.2217, lng: -110.9265 }, street: 'Speedway & Craycroft',
    transcript: "Dispatch, animal complaint at Speedway and Craycroft. Caller reporting a large tan female dog — no collar visible — pursuing a male subject on foot eastbound. Subject is running. Dog is fast. Very fast. Dog stopped, sitting, staring at a parked grey sedan. No plates visible. Unit responding." },
  { location: { lat: 32.2539, lng: -110.9977 }, street: 'Oracle & Wetmore',
    transcript: "TPD Central, dog following a bicyclist northbound on Oracle near Wetmore. Dog appears to be tracking the subject — not attacking, just following. Female, tan and white. She stopped at a dumpster behind the strip mall. Sniffing like she found something." },
  { location: { lat: 32.1954, lng: -110.8679 }, street: 'Kolb & Golf Links',
    transcript: "Animal control at Kolb and Golf Links. Dog matching previous description spotted again. She bit a subject attempting to enter a vehicle — subject fled. Dog is now sitting next to the vehicle. She is not moving. This is unusual behavior." },
  { location: { lat: 32.2348, lng: -110.9516 }, street: 'Campbell & River',
    transcript: "Animal complaint near Campbell and River. Large dog running full speed along the riverbed trail heading west. No owner in sight. Dog had what appeared to be a USB drive in her mouth. That is what the caller stated. Tag her if you can." },
  { location: { lat: 32.2101, lng: -110.9777 }, street: 'Congress & Stone',
    transcript: "Units downtown — tan female dog near Congress and Stone. Dog was seen at the parking garage entrance. Just watching. A resident approached her — her tag says ARYA. Chip address comes back to a VPN node. Repeat, the chip address is a VPN. How does a dog— stand by." },
];

export default function RadioFeed({ onNewIncident, userLocation, tier = 'free', onUpgradeClick, onAryaCall, onCallClick }: Props) {
  const feedRef  = useRef<HTMLDivElement>(null);
  const [items, setItems]       = useState<FeedItem[]>([]);
  const [loading, setLoading]   = useState(false);
  const [lastPoll, setLastPoll] = useState<Date | null>(null);
  const [callCount, setCallCount] = useState(0);
  const [expanded, setExpanded] = useState(true);
  const [headerProximity, setHeaderProximity] = useState(0); // 0 = far, 1 = on top edge
  const headerRef = useRef<HTMLDivElement>(null);
  const seenIds   = useRef(new Set<string>());
  const lastLocRef = useRef(userLocation);

  // Track mouse distance to top edge of header — pulse intensifies as user approaches
  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      const el = headerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const inHorizontalBand = e.clientX >= rect.left && e.clientX <= rect.right;
      if (!inHorizontalBand) { setHeaderProximity(0); return; }
      // Distance from cursor Y to top edge of the header (negative if above, positive if below)
      const distY = e.clientY - rect.top;
      // Activate within 120px above OR within the header itself
      const APPROACH = 120;
      if (distY < -APPROACH || distY > rect.height) { setHeaderProximity(0); return; }
      const d = Math.max(0, Math.min(APPROACH, -distY));
      setHeaderProximity(1 - d / APPROACH); // 1 when at top edge or inside
    };
    window.addEventListener('mousemove', onMove);
    return () => window.removeEventListener('mousemove', onMove);
  }, []);

  const pollLiveFeed = useCallback(async () => {
    const loc = userLocation ?? { lat: 32.2226, lng: -110.9747 };
    setLoading(true);
    try {
      const r = await fetch(
        `/api/calls/live/feed?lat=${loc.lat}&lon=${loc.lng}&radius=8&limit=30&hours=24`
      );
      if (!r.ok) return;
      const { feed, count } = await r.json() as { feed: LiveCall[]; count: number };

      setCallCount(count);
      const newItems: FeedItem[]   = [];
      const newIncidents: Incident[] = [];

      for (const c of feed) {
        if (seenIds.current.has(c.eid)) continue;
        seenIds.current.add(c.eid);

        const threat = levelToThreat(c.level, c.type);
        const item: FeedItem = {
          id:          c.eid,
          text:        c.text,
          type:        c.type || 'CALL FOR SERVICE',
          address:     (c.address || 'Tucson, AZ').replace(/\bTUC\b/i, 'TUCSON'),
          time:        c.time,
          threatLevel: threat,
          lat:         c.lat,
          lon:         c.lon,
          isLive:      true,
        };
        newItems.push(item);

        newIncidents.push({
          id:          c.eid,
          type:        item.type,
          description: c.text,
          location:    { lat: c.lat, lng: c.lon },
          timestamp:   c.ts * 1000,
          threatLevel: threat,
          source:      'RADIO',
        });
      }

      if (newItems.length > 0) {
        setItems(prev => [...newItems, ...prev].slice(0, 100));
        newIncidents.forEach(onNewIncident);
      }
      setLastPoll(new Date());
    } catch (e) {
      console.error('[ScannerFeed]', e);
    } finally {
      setLoading(false);
    }
  }, [userLocation, onNewIncident]);

  // Poll every 30s — picks up newly dripped calls
  useEffect(() => {
    pollLiveFeed();
    const interval = setInterval(pollLiveFeed, 30 * 1000);
    return () => clearInterval(interval);
  }, [pollLiveFeed]);

  // Re-poll when user moves significantly
  useEffect(() => {
    if (!userLocation || !lastLocRef.current) { lastLocRef.current = userLocation; return; }
    const dlat = Math.abs(userLocation.lat - lastLocRef.current.lat);
    const dlng = Math.abs(userLocation.lng - lastLocRef.current.lng);
    if (dlat > 0.007 || dlng > 0.007) {
      lastLocRef.current = userLocation;
      seenIds.current.clear();
      setItems([]);
      pollLiveFeed();
    }
  }, [userLocation, pollLiveFeed]);

  // Auto-scroll to top on new items
  useEffect(() => {
    if (feedRef.current) feedRef.current.scrollTop = 0;
  }, [items.length]);

  // ── Arya easter egg ──────────────────────────────────────────────────────────
  useEffect(() => {
    const delay = (4 + Math.random() * 6) * 60 * 1000;
    const fire = () => {
      const s = ARYA_SIGHTINGS[Math.floor(Math.random() * ARYA_SIGHTINGS.length)];
      const id = `arya-${Date.now()}`;
      setItems(prev => [{
        id, text: s.transcript, type: 'ANIMAL COMPLAINT', address: s.street,
        time: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
        threatLevel: ThreatLevel.LOW, lat: s.location.lat, lon: s.location.lng, isArya: true,
      }, ...prev].slice(0, 100));
      onNewIncident({
        id, type: 'ANIMAL COMPLAINT', description: s.transcript,
        location: s.location, timestamp: Date.now(), threatLevel: ThreatLevel.LOW, source: 'RADIO',
      });
      if (onAryaCall) onAryaCall(s.location);
      // TTS — only on first sighting per session, not every repeat
      if (window.speechSynthesis && !sessionStorage.getItem('arya-tts-fired')) {
        sessionStorage.setItem('arya-tts-fired', '1');
        const utter = new SpeechSynthesisUtterance(s.transcript);
        const voices = window.speechSynthesis.getVoices();
        const pick = voices.find(v => /samantha|karen|zira/i.test(v.name))
          ?? voices.find(v => v.lang.startsWith('en') && v.name.toLowerCase().includes('female'))
          ?? voices.find(v => v.lang.startsWith('en')) ?? voices[0];
        if (pick) utter.voice = pick;
        utter.rate = 0.88; utter.pitch = 0.8; utter.volume = 0.9;
        setTimeout(() => window.speechSynthesis.speak(utter), 800);
      }
      // Refire every 45-90 min — not every 18 min
      setTimeout(fire, (45 + Math.random() * 45) * 60 * 1000);
    };
    const t = setTimeout(fire, delay);
    return () => clearTimeout(t);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Idle-fade animation runs always; brightness ramps with proximity.
  // Title text glow opacity range: 0.55 (idle) → 1.0 (about to click)
  const baseTitleOpacity = 0.55 + 0.45 * headerProximity;
  const glowStrength = 4 + 16 * headerProximity; // px
  const arrowScale = 1 + 0.35 * headerProximity;
  const showArrow = headerProximity > 0.05; // appears when cursor is approaching/over

  return (
    <div className="flex flex-col h-full border-t" style={{ backgroundColor: '#000000', borderColor: '#d4a843' }}>
      {/* Collapse handle — appears on top edge when cursor approaches */}
      {showArrow && (
        <div
          onClick={() => setExpanded(e => !e)}
          className="absolute left-1/2 -translate-x-1/2 z-50 cursor-pointer select-none"
          style={{
            top: -14,
            opacity: headerProximity,
            transform: `translateX(-50%) scale(${arrowScale})`,
            transition: 'opacity 180ms ease-out, transform 180ms ease-out',
            pointerEvents: headerProximity > 0.15 ? 'auto' : 'none',
          }}
          title={expanded ? 'Collapse dispatch window' : 'Expand dispatch window'}
        >
          <div
            style={{
              background: '#000000',
              border: '1px solid #d4a843',
              borderRadius: 6,
              padding: '2px 10px',
              color: '#d4a843',
              fontSize: 12,
              fontFamily: 'JetBrains Mono, monospace',
              boxShadow: `0 0 ${8 + 12 * headerProximity}px rgba(212,168,67,${0.3 + 0.5 * headerProximity})`,
            }}
          >
            {expanded ? '▼ COLLAPSE' : '▲ EXPAND'}
          </div>
        </div>
      )}

      {/* Header */}
      <div
        ref={headerRef}
        className="flex items-center justify-between px-3 py-2 border-b cursor-pointer select-none relative"
        style={{ borderColor: '#d4a843', backgroundColor: '#000000' }}
        onClick={() => setExpanded(e => !e)}
      >
        <div className="flex items-center gap-2">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
          </span>
          <span
            className="font-orbitron text-xs font-bold tracking-widest tpd-title-pulse"
            style={{
              color: '#d4a843',
              opacity: baseTitleOpacity,
              textShadow: `0 0 ${glowStrength}px rgba(212,168,67,${0.5 + 0.5 * headerProximity})`,
              transition: 'opacity 180ms ease-out, text-shadow 180ms ease-out',
            }}
          >
            TPD LIVE DISPATCH
          </span>
          {loading && <span className="text-xs animate-pulse" style={{ color: '#d4a843' }}>SYNCING…</span>}
          {!loading && lastPoll && (
            <span className="text-xs font-mono" style={{ color: '#d4a843' }}>{lastPoll.toLocaleTimeString()}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <div
            onClick={(e) => {
              e.stopPropagation();
              if (onUpgradeClick) onUpgradeClick();
            }}
            className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full border border-[#ffd700]/50 bg-[#ffd700]/10 text-[#ffd700] text-[10px] font-mono hover:bg-[#ffd700]/25 transition-all cursor-pointer shadow-[0_0_10px_rgba(255,215,0,0.2)]"
            title="Sponsor a 3D Beacon for $25/mo"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#ffd700] animate-ping" />
            <span>★ 3D SPONSOR BEACON · $25/MO</span>
          </div>
          <span className="text-xs font-mono" style={{ color: '#d4a843' }}>{items.length} calls</span>
          <span className="text-xs" style={{ color: '#d4a843' }}>{expanded ? '▲' : '▼'}</span>
        </div>
      </div>

      {/* Feed list — slow collapse via max-height transition */}
      <div
        ref={feedRef}
        className="overflow-y-auto min-h-0"
        style={{
          maxHeight: expanded ? '4000px' : '0px',
          opacity: expanded ? 1 : 0,
          flex: expanded ? '1 1 auto' : '0 0 0px',
          transition: 'max-height 700ms ease-in-out, opacity 500ms ease-in-out',
        }}
      >
        {expanded && (
          <div className="h-full">
          {items.length === 0 && !loading && (
            <div className="text-xs font-mono px-3 py-4 text-center" style={{ color: 'rgba(212,168,67,0.55)' }}>
              {userLocation ? 'No active calls in your area.' : 'Set your location to load scanner feed.'}
            </div>
          )}
          {items.map(item => (
            <div
              key={item.id}
              onClick={() => onCallClick?.(item.lat, item.lon, item.address)}
              className="border-b px-3 py-2 transition-colors"
              style={{
                borderColor: 'rgba(212,168,67,0.18)',
                cursor: onCallClick ? 'pointer' : 'default',
              }}
              onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.backgroundColor = 'rgba(212,168,67,0.07)'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.backgroundColor = 'transparent'; }}
            >
              <div className="flex items-start gap-2">
                <span
                  className="mt-1 shrink-0 w-2 h-2 rounded-full"
                  style={{
                    backgroundColor: THREAT_COLOR[item.threatLevel],
                    boxShadow: item.isArya
                      ? '0 0 6px #ff00cc'
                      : `0 0 4px ${THREAT_COLOR[item.threatLevel]}80`,
                  }}
                />
                <div className="min-w-0 flex-1">
                  {/* Type badge */}
                  <div className="flex items-center justify-between mb-0.5">
                    <span className="text-[9px] font-bold font-mono uppercase tracking-wider px-1 py-0.5 rounded"
                      style={{
                        color: THREAT_COLOR[item.threatLevel],
                        backgroundColor: `${THREAT_COLOR[item.threatLevel]}18`,
                        border: `1px solid ${THREAT_COLOR[item.threatLevel]}40`,
                      }}
                    >
                      {item.isArya ? '🐕 ' : ''}{item.type}
                    </span>
                    <span className="text-[9px] font-mono shrink-0 ml-2" style={{ color: 'rgba(212,168,67,0.55)' }}>{item.time}</span>
                  </div>
                  {/* Scanner text line */}
                  <div className="text-xs font-mono leading-snug mb-0.5" style={{ color: '#e2d9c8' }}>
                    {item.text}
                  </div>
                  {/* Address */}
                  <div className="flex items-center gap-1">
                    <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ color: '#d4a843', flexShrink: 0 }}>
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
                    </svg>
                    <span className="text-xs truncate font-mono" style={{ color: '#d4a843' }}>{item.address}</span>
                    {onCallClick && (
                      <span className="text-[8px] font-mono ml-auto shrink-0" style={{ color: 'rgba(0,229,255,0.6)' }}>▶ MAP</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
          </div>
        )}
      </div>
    </div>
  );
}
